/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'preact/compat';
import { useRef } from 'preact/hooks';
import { Ref } from 'preact';
import { SwatchFlexView } from '#PRIVATE_SwatchFlexView';
import { ColorSwatch } from '#UNSAFE_ColorSwatch';
import { parseColorValue, type ColorValue } from '#utils/UNSAFE_color';
import { Flex } from '#UNSAFE_Flex/Flex';
import { Keys } from '#utils/UNSAFE_keys';
import { ListItemRendererContext } from '#UNSAFE_Collection';
import { TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { PaddingProps, paddingInterpolations } from '../utils/UNSAFE_interpolations/padding';
import { ColorProps, colorInterpolations } from '../utils/UNSAFE_interpolations/colors';
import { AriaProps, ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { sortPalette, convertPalette } from '#utils/UNSAFE_color';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import translations from '#resources/nls/en/bundle';
export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;

export type ColorPaletteProps = StyleProps &
  TestIdProps & {
    /**
     * The selected color of the palette
     */
    value?: ColorValue;

    /**
     * The swatch size indicates how large the color swatches are rendered.
     */
    swatchSize?: 'xs' | 'sm';

    /**
     * An array of colors for the palette
     */
    palette?: Array<ColorValue>;

    /**
     * Invoked when a swatch is toggled
     */
    onCommit?: (details: ValueUpdateDetail<ColorValue>) => void;
  };

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(ariaInterpolations),
  ...Object.values(paddingInterpolations)
];

const mergedInterpolations = mergeInterpolations<ColorPaletteProps>(interpolations);

/**
 * A Color Palette is an interface that displays a variety of color swatches allowing
 * users to select a color value.
 *
 * @param {ColorPaletteProps} props ColorPalette component props.
 * @returns {JSX.Element} ColorPalette component element.
 */
export const ColorPalette = forwardRef(
  (
    { value, swatchSize = 'sm', palette, onCommit, testId, ...props }: ColorPaletteProps,
    focusHandleRef?: Ref<FocusableHandle>
  ) => {
    const {
      class: mergedClasses,
      ariaLabel,
      ariaLabelledBy,
      ...mergedStyles
    } = mergedInterpolations(props);

    const rootRef = useRef<HTMLDivElement>(null);

    const testIdProps = useTestId(testId);
    const colorPaletteTranslated = translations.accColorPalette();

    const [current, setCurrent] = useState<string | undefined>(undefined);
    const [selected, setSelected] = useState<Keys<string | number>>({
      all: false,
      keys: new Set([(value as string) ?? undefined])
    });

    useEffect(() => {
      setSelected({
        all: false,
        keys: new Set([parseColorValue(value ?? undefined)])
      });
    }, [value]);

    useEffect(() => {}, [selected]); // redraw when selection occurs

    const getRowKey = (data: unknown) => {
      return data as string;
    };

    //Allows to call focus on rootRef without having to expose it
    useImperativeHandle(focusHandleRef!, () => ({
      focus: () => {
        if (rootRef.current) {
          rootRef.current.focus();
        }
      },
      blur: () => {
        if (rootRef.current) {
          rootRef.current.blur();
        }
      }
    }));

    const dropdownStyle = {
      width: '100%',
      height: '100%'
    };

    return (
      <div
        aria-labelledby={ariaLabelledBy}
        className={mergedClasses}
        style={dropdownStyle}
        {...testIdProps}
        {...mergedStyles}>
        <Flex align="center">
          <SwatchFlexView
            ref={rootRef}
            aria-label={ariaLabel ? ariaLabel : colorPaletteTranslated}
            focusBehavior="content"
            data={palette ? convertPalette(sortPalette(palette)) : null}
            getRowKey={getRowKey}
            currentKey={current}
            onCurrentKeyChange={(detail) => {
              setCurrent(detail.value as string);
            }}
            selectedKeys={selected}>
            {(context: ListItemRendererContext<string | number, unknown>) => (
              <ColorSwatch
                size={swatchSize}
                testId={testId + '_palette' + '_colorSwatch_' + context.data}
                aria-hidden={true}
                value={context.data as ColorValue}
                onToggle={() => {
                  onCommit?.({
                    value: context.data as ColorValue,
                    previousValue: selected.keys?.values().next().value as ColorValue
                  });
                  setSelected({ all: false, keys: new Set([context.data as string]) });
                }}
                isSelected={selected.keys?.has(context.data as string)}></ColorSwatch>
            )}
          </SwatchFlexView>
        </Flex>
      </div>
    );
  }
);

ColorPalette.displayName = 'ColorPalette';
